/**
 * 列定義エディタコンポーネント
 *
 * CSV列の表示・編集用テーブルUI
 */

import {
  Paper,
  Group,
  Text,
  Button,
  TextInput,
  Table,
  ActionIcon,
  Center,
  Popover,
  Stack,
  Alert,
} from "@mantine/core";
import { Plus, Trash2, ArrowUp, ArrowDown, Info } from "lucide-react";
import { CsvColumn } from "../../services/csvSettingsService";

interface ColumnDefinitionEditorProps {
  columns: CsvColumn[];
  onAddColumn: () => void;
  onUpdateColumn: (index: number, updates: Partial<CsvColumn>) => void;
  onRemoveColumn: (index: number) => void;
  onMoveColumn: (index: number, direction: "up" | "down") => void;
}

/**
 * 列定義エディタ
 */
export function ColumnDefinitionEditor({
  columns,
  onAddColumn,
  onUpdateColumn,
  onRemoveColumn,
  onMoveColumn,
}: ColumnDefinitionEditorProps) {
  return (
    <Paper withBorder p="md" bg="var(--mantine-color-default-hover)">
      <Group justify="space-between" mb="xs">
        <Text fw={500} size="sm">
          列定義
        </Text>
        <Button size="xs" variant="default" leftSection={<Plus size={14} />} onClick={onAddColumn}>
          列を追加
        </Button>
      </Group>

      <Table bg="var(--mantine-color-body)" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 60 }}>順序</Table.Th>
            <Table.Th>ヘッダー名</Table.Th>
            <Table.Th style={{ width: 80 }}>
              <Group gap={2}>
                必須
                <Popover width={280} position="bottom" withArrow shadow="md">
                  <Popover.Target>
                    <ActionIcon variant="transparent" size="xs" color="gray" aria-label="詳細">
                      <Info size={14} />
                    </ActionIcon>
                  </Popover.Target>
                  <Popover.Dropdown>
                    <Stack gap="xs">
                      <Text size="xs" fw={700}>
                        必須チェック機能について
                      </Text>
                      <Text size="xs">
                        チェックを入れると、CSV生成時にこの項目の値が空欄だった場合、エラーとして通知されます。
                      </Text>
                      <Alert color="blue" variant="light" pt="xs" pb="xs">
                        <Text size="xs">
                          <strong>注意:</strong>{" "}
                          AIに対して「必ず読み取るように」と命令する機能ではありません。あくまで出力結果のチェック（バリデーション）を行うための設定です。
                        </Text>
                      </Alert>
                    </Stack>
                  </Popover.Dropdown>
                </Popover>
              </Group>
            </Table.Th>
            <Table.Th style={{ width: 60 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {columns.map((col, index) => (
            <Table.Tr key={index}>
              <Table.Td>
                <Group gap={2}>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => onMoveColumn(index, "up")}
                    disabled={index === 0}
                  >
                    <ArrowUp size={12} />
                  </ActionIcon>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    onClick={() => onMoveColumn(index, "down")}
                    disabled={index === columns.length - 1}
                  >
                    <ArrowDown size={12} />
                  </ActionIcon>
                </Group>
              </Table.Td>
              <Table.Td>
                <TextInput
                  size="xs"
                  value={col.headerName}
                  onChange={(e) => onUpdateColumn(index, { headerName: e.currentTarget.value })}
                />
              </Table.Td>
              <Table.Td>
                <Center>
                  <input
                    type="checkbox"
                    checked={col.required}
                    onChange={(e) => onUpdateColumn(index, { required: e.target.checked })}
                  />
                </Center>
              </Table.Td>
              <Table.Td>
                <ActionIcon color="red" variant="subtle" onClick={() => onRemoveColumn(index)}>
                  <Trash2 size={14} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
